import React, { ReactNode, useEffect, useState } from 'react';
import type { Chunk } from 'views/Aide/Scenario/PolicyTable';
import { flatBlockLevelDescendant } from 'components/TemplateEditor/utils/slateNodeHelper';
import { Text } from 'slate';
import Icon from '@ant-design/icons';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import DividingLine from 'components/TemplateEditor/renders/DividingLine';
import TagConcept from 'components/TemplateEditor/renders/TagConcept';
import { Tag, Tooltip } from 'antd';
import { css } from '@emotion/css';
import { ReactComponent as EmojiSvg } from '../../assets/emoji.svg';
import { ReactComponent as ImageSvg } from '../../assets/picture.svg';
import { ReactComponent as VideoSvg } from '../../assets/video.svg';
import { flatten } from 'lodash-es';
import useMustacheSticker2Url from 'hooks/business/useMustacheSticker2Url';
import { usePreviewModalContext } from 'views/Conversation/Sider/PreviewModal';
import { useTranslate } from '@tolgee/react';
import { ItemPreview } from 'components/EditorPreviewer';

interface ChunkRenderProps {
  chunks: Chunk[];
  platform: string;
  region: string;
  textExtra?: ReactNode;
  item?: ReactNode;
  voucher?: ReactNode;
  language?: General.Language;
}

export const useChunkRender = (props: ChunkRenderProps) => {
  const { chunks, platform, region, textExtra, item, voucher, language } = props;
  const [getStickerUrl] = useMustacheSticker2Url(platform as any, region as any);
  const textChunk = React.useMemo(() => chunks.find((item) => item.type === 'text'), [chunks]);
  const stickerChunk = React.useMemo(
    () => chunks.find((item) => item.type === 'sticker'),
    [chunks],
  );
  const imageChunk = React.useMemo(() => chunks.find((item) => item.type === 'image'), [chunks]);
  const videoChunk = React.useMemo(() => chunks.find((item) => item.type === 'video'), [chunks]);
  const itemChunk = React.useMemo(() => chunks.find((item) => item.type === 'item'), [chunks]);
  const voucherChunk = React.useMemo(
    () => chunks.find((item) => item.type === 'voucher'),
    [chunks],
  );

  const { show } = usePreviewModalContext();
  const { t } = useTranslate();

  const [images, setImages] = useState<string[]>([]);
  const [videos, setVideos] = useState<string[]>([]);

  const textNode = React.useMemo(() => {
    if (!textChunk) return null;
    const { content } = textChunk;
    const text = flatBlockLevelDescendant(content).reduce<any[]>((result, node, index) => {
      const key = `${node.type}_${index}`;
      if (Text.isText(node)) {
        result.push(<span key={key}>{node.text}</span>);
        return result;
      }
      switch (node.type) {
        case CustomSlateNodeType.DIVIDING_LINE:
          result.push(<DividingLine key={key} />);
          break;
        case CustomSlateNodeType.CONCEPT:
          if (node.label === '好评') {
            result.push(<span>{` 1 `}</span>);
            break;
          }

          if (node.label === '差评') {
            result.push(<span>{` 2 `}</span>);
            break;
          }

          result.push(
            <TagConcept key={key} element={node} attributes={{}} language={language}>
              {null}
            </TagConcept>,
          );
          break;
        case CustomSlateNodeType.PARAMTEXT:
          result.push(
            <Tag key={key} color="#E6E4F2" className={styles.paramTextTag}>
              {node.label}
            </Tag>,
          );
          break;
        case CustomSlateNodeType.PARAMHOLDER:
          result.push(
            <Tag key={key} color="#E6E4F2" className={styles.paramTextTag}>
              {node.label}
            </Tag>,
          );
          break;
        default:
          break;
      }
      return result;
    }, []);
    return [...text, textExtra];
  }, [textChunk, textExtra, language]);

  useEffect(() => {
    const chunkList = flatten([stickerChunk, imageChunk, videoChunk]).filter(Boolean);
    const tempVideos: string[] = [];
    const teapImages: Array<{ chunk: Chunk | undefined; imageId: string }> = [];
    chunkList.forEach((chunk) => {
      chunk!.content.forEach((item) => {
        if (item.type === CustomSlateNodeType.IMAGE) {
          item.images.forEach(async (image) => {
            if (image.imageId) {
              if (chunk!.type === 'video') {
                tempVideos.push(image.imageId);
              } else {
                teapImages.push({ chunk, imageId: image.imageId });
              }
            }
          });
        }
      });
    });
    setVideos(tempVideos);
    Promise.all(
      teapImages.map(({ chunk, imageId }) => {
        return new Promise<string>((resolve) => {
          if (chunk!.type === 'sticker') {
            getStickerUrl(imageId)
              .then((url) => {
                resolve(url || imageId);
              })
              .catch(() => {
                resolve(imageId);
              });
          } else {
            resolve(imageId);
          }
        });
      }),
    ).then(setImages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stickerChunk, imageChunk, videoChunk]);

  const showPreview = (initialIndex = 0) =>
    show({
      imageFirst: true,
      images,
      videos,
      initialIndex,
    });
  const stickerNode = stickerChunk ? (
    <Tooltip title={t('review.点击查看')}>
      <div
        className="rounded-[2px] border border-solid border-[var(--fett-border-default)] px-[6px] py-[1px] w-[fit-content] flex items-center justify-center cursor-pointer"
        onClick={() => showPreview()}
      >
        <Icon component={EmojiSvg} style={{ width: '22px' }} />
        <div className="ml-[3px]">{stickerChunk.content?.length}</div>
      </div>
    </Tooltip>
  ) : null;

  const imageNode = imageChunk ? (
    <Tooltip title={t('review.点击查看')}>
      <div
        className="rounded-[2px] border border-solid border-[var(--fett-border-default)] px-[6px] py-[1px] w-[fit-content] flex items-center justify-center cursor-pointer"
        onClick={() => showPreview(stickerChunk?.content?.length || 0)}
      >
        <Icon component={ImageSvg} style={{ width: '22px' }} />
        <div className="ml-[3px]">{imageChunk.content?.length}</div>
      </div>
    </Tooltip>
  ) : null;

  const videoNode = videoChunk ? (
    <Tooltip title={t('review.点击查看')}>
      <div
        className="rounded-[2px] border border-solid border-[var(--fett-border-default)] px-[6px] py-[1px] w-[fit-content] flex items-center justify-center cursor-pointer"
        onClick={() =>
          showPreview((stickerChunk?.content?.length || 0) + (imageChunk?.content?.length || 0))
        }
      >
        <Icon component={VideoSvg} style={{ width: '22px' }} />
        <div className="ml-[3px]">{videoChunk.content?.length}</div>
      </div>
    </Tooltip>
  ) : null;

  const itemNode = itemChunk
    ? item || (
        <div className={styles.itemWrapper}>
          <ItemPreview title={t('商品标题')} />
        </div>
      )
    : null;
  const voucherNode = voucherChunk ? voucher : null;

  if (chunks.length === 0) {
    return <>-</>;
  }

  return (
    <div>
      {textNode && <div className="mb-2 whitespace-pre-wrap">{textNode}</div>}
      <div className="flex gap-[8px] items-end empty:mt-[-8px]">
        {itemNode}
        {stickerNode}
        {imageNode}
        {videoNode}
      </div>
      {voucherNode ? <div className="mt-2">{voucherNode}</div> : null}
    </div>
  );
};

export const ChunkRender = (props: ChunkRenderProps) => {
  return useChunkRender(props);
};

const styles = {
  showAllContent: css`
    width: 100%;
    word-break: break-all;
  `,
  content: css`
    height: 48px;
    width: 100%;
    line-height: 24px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: pre-wrap;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `,
  tag: css`
    background-color: var(--fett-bg-app);
    border: 0;
    color: var(--fett-text-secondary);
  `,
  paramTextTag: css`
    color: var(--fett-scenario-purple-text);
  `,
  emoticon: css`
    display: inline;
  `,
  itemWrapper: css`
    padding: 6px;
    border: solid 1px var(--fett-border-default);
    border-radius: 2px;
  `,
};
